import { SectionWrapper } from '../../../../components/layout/section-wrapper';
import { useGetEosConsumptionByEnergyTypeQuery } from '@client/api';
import { useDashboardFilters } from '../../../dashboard/context/dashboard-filters.context';
import { useMemo, useState } from 'react';
import { useTagLabel } from '../../../../utils/eos/getTagLabel';
import { SeriesDataPoint, StackedBarChart } from '../../../../components/charts/stacked-bar-chart';
import { useChartResolutionDateFormat } from '../../../../components/charts/hooks/useChartLabelFormat';
import { getSeriesColor } from '../../../../utils/eos/getSeriesColor';
import { ChartLegend } from '../../../../components/charts/types';
import { BasicChartTooltipProps, BasicChartTooltip } from '../../../../components/charts/tooltip';
import { ChartSectionState } from '../../../../components/charts/chart-section-state';
import { useTranslation } from 'react-i18next';
import { utcToZone } from '../../../../utils/date/utcToZone';
import { useEosSourcesFilterContext } from '../../../../components/eos/filters/eos-sources-filter';

export function useConsumptionByEnergyTypeQuery() {
  const getTagLabel = useTagLabel();
  const { sources } = useEosSourcesFilterContext();
  const { selectedSpaceIds, selectedDateFromIso, selectedDateToIso } = useDashboardFilters();
  const { data, isLoading, isError } = useGetEosConsumptionByEnergyTypeQuery(
    {
      from: selectedDateFromIso,
      to: selectedDateToIso,
      spaceIds: selectedSpaceIds,
      sources: sources,
    },
    {
      skip: !selectedDateFromIso || !selectedDateToIso || sources.length === 0,
    },
  );
  const { resolution, tags = [], buckets = [], seriesTags = [], series = [] } = data ?? {};

  const legends = useMemo(() => {
    const filteredTagsLegends = tags.filter((tag) => tag !== 'energy');
    const barLegends = filteredTagsLegends.map<ChartLegend>((tag) => ({
      id: tag,
      label: getTagLabel(tag),
      color: getSeriesColor(tag),
    }));

    const seriesLegends = seriesTags
      .filter((tag) => tag !== 'energyPrice')
      ?.map<ChartLegend>((tag) => ({
        id: tag,
        label: getTagLabel(tag),
        color: getSeriesColor(tag),
      }));

    return { barLegends, seriesLegends };
  }, [tags, seriesTags, getTagLabel]);

  const seriesData = useMemo<SeriesDataPoint[]>(() => {
    if (!series) return [];
    return series.map((item) => {
      const withYAxis = item.tag.includes('energyPrice') || item.tag.includes('temperatureOut');
      return {
        key: item.tag,
        unit: item.unit!,
        label: getTagLabel(item.tag),
        yAxis: withYAxis,
        reverseAxis: item.tag.includes('temperatureOut'),
        minSpan: item.tag.includes('energyPrice') ? 1 : undefined,
        data: item.buckets?.map((point) => ({ key: point.key, value: point.value })) ?? [],
      } satisfies SeriesDataPoint;
    });
  }, [series, getTagLabel]);

  return {
    resolution,
    data: buckets ?? [],
    seriesData: seriesData ?? [],
    tags,
    legends,
    isLoading,
    isError,
  };
}

export function ConsumptionGraphSection() {
  const { t } = useTranslation('eos');
  const getTagLabel = useTagLabel();
  const [activeSeries, setActiveSeries] = useState<string[]>(['temperatureOut']);

  const { data, tags, seriesData, legends, resolution, isLoading, isError } = useConsumptionByEnergyTypeQuery();
  const { rotation, getFormat } = useChartResolutionDateFormat(resolution);

  const handleSetActiveSeries = (seriesId: string) => {
    setActiveSeries((prev) => {
      if (prev.includes(seriesId)) {
        return prev.filter((id) => id !== seriesId);
      }
      return [...prev, seriesId];
    });
  };

  const filteredKeys = useMemo(() => {
    return tags.filter((tag) => tag !== 'energy');
  }, [tags]);

  if (isLoading) {
    return <ChartSectionState className="aspect-[10/4]">{t('loading')}</ChartSectionState>;
  }

  if (isError) {
    return <ChartSectionState className="aspect-[10/4]">{t('errorLoadingData')}</ChartSectionState>;
  }

  if (!data || data.length === 0) {
    return <ChartSectionState className="aspect-[10/4]">{t('noDataAvailable')}</ChartSectionState>;
  }

  const renderTooltip = (props: any) => {
    let timeLabel: string = '';
    if (resolution === 'hourly') {
      const date = utcToZone(props.indexValue as string);
      timeLabel = `${date.subtract(1, 'hour').format('HH:mm')} - ${date.format('HH:mm')}`;
    } else {
      timeLabel = getFormat(props.indexValue as string);
    }

    const tooltipProps: BasicChartTooltipProps = {
      label: getTagLabel(props.id as string),
      timeLabel,
      value: props.formattedValue,
      color: getSeriesColor(props.id as string),
      unit: 'kWh',
    };

    return <BasicChartTooltip {...tooltipProps} />;
  };

  return (
    <SectionWrapper>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex w-full aspect-[10/4]">
          <StackedBarChart
            activeSeries={activeSeries}
            setActiveSeries={handleSetActiveSeries}
            keys={filteredKeys}
            legendsBar={legends.barLegends}
            legendsSeries={legends.seriesLegends}
            bars={data}
            series={seriesData}
            yLabel="kWh"
            tooltip={renderTooltip}
            xLabelRotation={rotation}
            getXLabelFormat={getFormat}
            getColor={getSeriesColor}
          />
        </div>
      </div>
    </SectionWrapper>
  );
}
